import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import { Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import camelcaseKeys from 'camelcase-keys';

import FA from 'utils/FA';
import {
  reviewContactsFilters,
  reviewContacts as reviewContactsDuck
} from 'ducks';
import {
  duckify,
  singleDuck,
  currentUser as currentUserDuck
} from 'utils/duckify';
import Filters from 'components/Filters/Filters';
import ContactList from 'components/ContactList/ContactList';
import MyReviewsContactListNav from './MyReviewsContactListNav';
import Routes from '../../../routes';
import withPagination from '../../hocs/withPagination';
import withSorting from '../../hocs/withSorting';
import withFiltering from '../../hocs/withFiltering';
import './styles/MyReviewsContactList.scss';

const emptyContacts = {
  pending: true,
  total: 0,
  perPage: 50,
  search: {},
  additionalFields: {
    fields: [],
    filter_fields: []
  }
};

const reviewContactsDenormalizer = response => {
  const result = response.result.review_contacts.map(contact => response.entities.reviewContacts[contact]);
  Object.keys(camelcaseKeys(response.result.meta)).forEach(key => {
    result[key] = camelcaseKeys(response.result.meta)[key];
  });

  return result;
};

const MyReviewsContactList = ({
  reviewYear,
  filter,
  currentUser,
  allDucksReady,
  location,
  router,
  sort,
  page,
  filters,
  loadContacts,
  onSort,
  setPage,
  setFilters
}) => {
  const [complete, setComplete] = useState(queryString.parse(location.search).complete === 'true');
  const [reviewContacts, setReviewContacts] = useState(emptyContacts);
  const [clear, setClear] = useState(false);

  const onClickAssigned = (e) => {
    e.preventDefault();
    setComplete(false);
  }

  const onClickCompleted = (e) => {
    e.preventDefault();
    setComplete(true);
  }


  const updateFilters = newFilters => {
    /** If filters have been cleared */
    if (Object.keys(newFilters).length === 0) {
      setClear(true);
    }
    setFilters(newFilters);
  };

  const fetchContacts = () => {
    const params = queryString.parse(location.search);
    const query = {
      complete: complete || false,
      school_year_id: reviewYear.id,
      filters: clear ? {} : params.filters,
      meta: true,
      ...params
    };

    loadContacts({ ...query, clear }, queryString.stringify(query))
      .then(contacts => {
        setReviewContacts(contacts);
        setClear(false);
      });
  };

  useEffect(() => {
    if (allDucksReady) {
      setReviewContacts(emptyContacts);
      fetchContacts();
    }
  }, [location.search]);

  useEffect(() => {
    if (allDucksReady) {
      setFilters(filter.filters);
      setReviewContacts(emptyContacts);
      fetchContacts();
    }
  }, [allDucksReady]);

  useEffect(() => {
    if (allDucksReady) {
      const urlParams = new URLSearchParams(location.search);
      urlParams.set('complete', complete);
      router.push(`${location.pathname}?${urlParams.toString()}`);
    }
  }, [complete]);

  const OverlayTriggerColumn = ({ children, overlayTrigger }) => (
    overlayTrigger ? (
      <OverlayTrigger
        placement='top'
        overlay={(
          <Tooltip id='review-rating-tooltip'>
            Rating criteria does not apply to this student
          </Tooltip>
        )}
      >
        {children}
      </OverlayTrigger>
    ) : (
      children
    )
  );

  OverlayTriggerColumn.propTypes = {
    /** Children special prop */
    children: PropTypes.node.isRequired,

    /** Boolean whether or not to wrap column */
    overlayTrigger: PropTypes.bool
  };

  OverlayTriggerColumn.defaultProps = {
    overlayTrigger: false
  };

  const generateColumns = fields => {
    const ratingRegex = new RegExp('_review_rating.*');
    const columns = [
      {
        header: '',
        type: 'button',
        value: contact => (
          <td className='review-view-column'>
            <Button
              href={Routes.contact_review_path(contact.id, { schoolYearId: reviewYear.id })}
              className='review-contact-list__tag-button'
              variant='success'
              size='xs'
            >
              {complete ? 'View' : 'Review'}
            </Button>
          </td>
        )
      }];

    fields.forEach(field => {
      let column = {};
      if (!field.hidden) {
        column = {
          header: field.display_name,
          sortName: (field.sortable ? field.name : undefined),
          value: (contact) => {
            if (field.name === 'display_name' && currentUser.acl > 0) {
              return (
                <td className={`review-field-${field.name}`}>
                  <a href={Routes.contact_path(contact.id)}>
                    {contact[field.name]}
                  </a>
                </td>
              );
            } else if (field.name === 'review_comments'
              || (field.name.startsWith('calendar_') && field.name.endsWith('_date'))) {
              return (
                <td className={`review-field-${field.name}`}>
                  <div dangerouslySetInnerHTML={{ __html: contact[field.name] }} />
                </td>
              );
            }
            return (
              <td className={`review-field-${field.name}`}>
                <OverlayTriggerColumn
                  overlayTrigger={(
                    ratingRegex.exec(field.name)
                    && contact[field.name] === '-'
                  )}
                >
                  <div>{contact[field.name]}</div>
                </OverlayTriggerColumn>
              </td>
            );
          }
        };
        columns.push(column);
      }
    });

    return columns;
  };

  return (allDucksReady && (
    <React.Fragment>
      <MyReviewsContactListNav
        totalPages={Math.ceil(reviewContacts.total / reviewContacts.perPage)}
        page={page}
        reviewComplete={complete}
        setPage={setPage}
        onClickAssigned={onClickAssigned}
        onClickCompleted={onClickCompleted}
      />
      <Filters
        sublabel={(
          <Button
            href={Routes.search_path('', { criteria_json: JSON.stringify(reviewContacts.search) })}
            variant='link'
            size='xs'
            className='review-contact-list__filter-link'
          >
            <FA icon={faSearch} nbsp />
            View in Search
          </Button>
        )}
        fields={(
          reviewContacts.additionalFields.filter_fields
            .filter(field => field.option_prompts.length > 0)
            .map(field => ({
              ...field,
              display_name: field.prompt,
              options: field.options.map((option, i) => [field.option_prompts[i], option])
            }))
        )}
        selected={filters}
        submitFilters={updateFilters}
        itemsName='contacts'
      />
      <ContactList
        columns={generateColumns(reviewContacts.additionalFields.fields)}
        emptyMessage='No contacts meet the selected criteria.'
        contacts={reviewContacts}
        onSort={onSort}
        sort={sort}
      />
    </React.Fragment>
  ));
};

MyReviewsContactList.propTypes = {
  /** filter Duck */
  filter: PropTypes.shape({
    id: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({})),
    filter_fields: PropTypes.arrayOf(PropTypes.shape({}))
  }),

  /** current Review Year */
  reviewYear: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    review_enabled: PropTypes.bool
  }),

  /** current user Duck */
  currentUser: PropTypes.shape({
    id: PropTypes.string,
    acl: PropTypes.number
  }),

  /** withSorting, withPagination, withRouter, and withFilters HOC objects and functions */
  sort: PropTypes.shape({
    column: PropTypes.string,
    order: PropTypes.string
  }).isRequired,
  page: PropTypes.number.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  router: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  filters: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(
      PropTypes.string
    )
  }),
  onSort: PropTypes.func,
  setPage: PropTypes.func,
  setFilters: PropTypes.func,

  /** allDucksReady prop */
  allDucksReady: PropTypes.bool
};

MyReviewsContactList.defaultProps = {
  reviewYear: {},
  currentUser: {},
  filters: {},
  allDucksReady: false,
  onSort: () => false,
  setPage: () => false,
  setFilters: () => false
};

const mapDucksToProps = ({ reviewYear }) => ({
  filter: singleDuck(reviewContactsFilters, reviewYear.id),
  currentUser: currentUserDuck
});

const mapDispatchToProps = dispatch => ({
  loadContacts: (query, stateKey) => dispatch(reviewContactsDuck.actions.load(query, stateKey))
    .then(reviewContactsDenormalizer)
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({ column: 'display_name', order: 'asc' }),
  duckify(mapDucksToProps, () => {}, mapDispatchToProps)
)(MyReviewsContactList);
